import { useEffect, useRef, useState } from "react";

interface MockPlayerProps {
  cover: string;
  title: string;
  duration: string;
}

type PlayerState = "idle" | "loading" | "playing" | "paused" | "error";

const toSeconds = (duration: string) => {
  const [minutes, seconds] = duration.split(":").map((item) => Number(item));
  return (minutes || 0) * 60 + (seconds || 0);
};

const formatTime = (total: number) => {
  const minutes = Math.floor(total / 60);
  const seconds = Math.floor(total % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export default function MockPlayer({ cover, title, duration }: MockPlayerProps) {
  const total = toSeconds(duration);
  const [state, setState] = useState<PlayerState>("idle");
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (state !== "playing") return undefined;
    timerRef.current = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          window.clearInterval(timerRef.current ?? undefined);
          setState("paused");
          return 100;
        }
        return prev + 100 / (total || 1);
      });
    }, 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [state, total]);

  const start = () => {
    if (state === "error") return;
    setState("loading");
    window.setTimeout(() => {
      setState((prev) => (prev === "loading" ? "playing" : prev));
    }, 1100);
  };

  const togglePlay = () => {
    if (state === "playing") {
      setState("paused");
      return;
    }
    if (state === "idle" || state === "paused") {
      start();
      return;
    }
    if (state === "loading") setState("idle");
  };

  const restart = () => {
    setProgress(0);
    setState("error");
  };

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
        <img
          src={cover}
          alt={`${title} - 视频封面`}
          title={`${title} - 抖阳视频内容`}
          className={`h-full w-full object-cover object-top transition-all duration-500 ${
            state === "playing" ? "scale-105 opacity-70" : "opacity-100"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-foreground-950/40" />

        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="rounded bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
            抖阳视频
          </span>
          <span className="rounded border border-background-50/40 px-2 py-1 text-[11px] text-background-100">
            演示播放器
          </span>
        </div>

        {state === "loading" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/60">
            <span className="h-9 w-9 animate-spin rounded-full border-2 border-background-50/30 border-t-background-50" />
            <p className="text-sm text-background-100">视频加载中（演示）…</p>
          </div>
        ) : null}

        {state === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/80 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-50/10 text-background-50">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <p className="text-base font-medium text-background-50">视频不可用</p>
            <p className="max-w-sm text-xs leading-relaxed text-background-300">
              当前为演示播放器，无法加载真实视频流。你可以重新打开演示播放，或继续阅读视频简介。
            </p>
            <button
              type="button"
              onClick={() => {
                setProgress(0);
                start();
              }}
              className="mt-1 cursor-pointer whitespace-nowrap rounded-md bg-background-50 px-4 py-2 text-xs text-foreground-950"
            >
              重新加载演示
            </button>
          </div>
        ) : null}

        {state === "idle" || state === "paused" ? (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="播放演示视频"
            className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform duration-300 hover:scale-110">
              <i className={`${state === "paused" ? "ri-play-fill" : "ri-play-fill"} text-3xl`} />
            </span>
            <span className="rounded-full bg-foreground-950/55 px-3 py-1 text-[11px] text-background-100">
              {progress > 0 ? "继续演示播放" : "点击播放（演示，无真实视频流）"}
            </span>
          </button>
        ) : null}

        {state === "playing" ? (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 rounded-md bg-foreground-950/45 px-3 py-2 text-[11px] text-background-100">
            <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary-400" />
            演示播放中 · 非真实视频画面
          </div>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
          <div className="mb-2.5 h-1 w-full overflow-hidden rounded-full bg-background-50/25">
            <div
              className="h-full rounded-full bg-primary-400 transition-all duration-1000"
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
          <div className="flex items-center gap-3 text-background-100">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={state === "playing" ? "暂停" : "播放"}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-background-50/15"
            >
              <i className={`${state === "playing" ? "ri-pause-fill" : "ri-play-fill"} text-base`} />
            </button>
            <span className="font-label text-[11px]">
              {formatTime((progress / 100) * total)} / {duration}
            </span>
            <button
              type="button"
              onClick={() => setMuted((prev) => !prev)}
              aria-label={muted ? "取消静音" : "静音"}
              className="ml-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-background-50/15"
            >
              <i className={`${muted ? "ri-volume-mute-line" : "ri-volume-up-line"} text-base`} />
            </button>
            <button
              type="button"
              onClick={restart}
              className="cursor-pointer rounded border border-background-50/30 px-2 py-1 text-[10px] text-background-200 hover:bg-background-50/10"
            >
              模拟异常
            </button>
            <button
              type="button"
              aria-label="全屏（演示）"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-background-50/15"
            >
              <i className="ri-fullscreen-line text-base" />
            </button>
          </div>
        </div>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground-400">
        <i className="ri-information-line" />
        本播放器仅为界面演示，不加载真实视频流，也不提供下载功能。
      </p>
    </div>
  );
}